import React, { useEffect, useMemo, useState } from "react";

import Button from "../../Button";
import Form from "../../Form/Form";
import Modal from "../../Modal";

import useAlert from "../../../hooks/useAlert";
import GenericPageModalForm from "./GenericPageModalForm";
import PartManagementModalForm from "./PartManagementModalForm";
import ProductManagementModalForm from "./ProductManagementModalForm";
import QuotationModalForm from "./QuotationModalForm";
import PurchaseOrderModalForm from "./PurchaseOrderModalForm";
import ImageManagementUploadForm from "./ImageManagementUploadForm";
import {
  calculateVolumeFromRow,
  clearInactiveDimensions,
  convertDimensionValue,
  groupFieldsBySection,
  normalizeUnit,
  orderFormFields,
  PO_ITEM_DEFAULT,
  PART_SPECIFICATION_DEFAULT,
  PRODUCT_PART_DEFAULT,
  QUOTATION_COST_DEFAULTS,
  QUOTATION_ITEM_DEFAULT,
} from "./pageModal.utils";

import styles from "./pageModal.module.css";

const EMPTY_ARRAY = Object.freeze([]);

const getFieldLabel = (field) => (
  <>
    {field.label} {field.required && <span style={{ color: "red" }}>*</span>}
  </>
);

const normalizeToken = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const toNonNegativeNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return 0;
  return numericValue;
};

const getTodayDateForInput = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const getQuotationItemsSubtotal = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((sum, row) => {
    return sum + toNonNegativeNumber(row?.amount);
  }, 0);

const getQuotationCostValues = (source = {}, items = []) => {
  const manufacturingCost = toNonNegativeNumber(source.manufacturing_cost);
  const packagingCost = toNonNegativeNumber(source.packaging_cost);
  const fobCost = toNonNegativeNumber(source.fob_cost);
  const finishingCost = toNonNegativeNumber(source.finishing_cost);
  const tax = toNonNegativeNumber(source.tax);
  const itemsSubtotal = getQuotationItemsSubtotal(items);
  const totalAmount =
    itemsSubtotal +
    manufacturingCost +
    packagingCost +
    fobCost +
    finishingCost +
    tax;

  return {
    manufacturing_cost: String(manufacturingCost),
    packaging_cost: String(packagingCost),
    fob_cost: String(fobCost),
    finishing_cost: String(finishingCost),
    tax: String(tax),
    total_amount: String(totalAmount),
  };
};

const PageModal = ({ show, onClose, config, mode, data, onSave }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [productPartRows, setProductPartRows] = useState([PRODUCT_PART_DEFAULT]);
  const [partSpecificationRows, setPartSpecificationRows] = useState([
    PART_SPECIFICATION_DEFAULT,
  ]);
  const [quotationItems, setQuotationItems] = useState([QUOTATION_ITEM_DEFAULT]);
  const [poItems, setPoItems] = useState([]);
  const [poStep, setPoStep] = useState(1);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedPoQuotations, setSelectedPoQuotations] = useState([]);

  const { showAlert } = useAlert();

  const baseFormFields = config?.form ?? EMPTY_ARRAY;
  const pageCode = String(config?.code || config?.model_name || "").toLowerCase();
  const pageCodeToken = normalizeToken(pageCode);
  const customFormToken = normalizeToken(config?.custom_form || "");
  const isRolePagePermissionPage =
    pageCodeToken === "rolepagepermission" || pageCodeToken === "rolepagepermissions";
  const roleOptionsByEnterprize = config?.meta?.role_options_by_enterprize || {};
  const roleFieldName =
    baseFormFields.find(
      (field) =>
        field?.type === "select" &&
        normalizeToken(field?.name).includes("role") &&
        !normalizeToken(field?.name).includes("page"),
    )?.name || "role_fid";
  const selectedEnterprizeKey = String(formData.enterprize_fid || "");
  const formFields = useMemo(() => {
    if (!isRolePagePermissionPage) return baseFormFields;
    const scopedRoleOptions = roleOptionsByEnterprize[selectedEnterprizeKey] || [];
    return baseFormFields.map((field) => {
      if (field.name !== roleFieldName || field.type !== "select") return field;
      return {
        ...field,
        options: scopedRoleOptions,
      };
    });
  }, [
    baseFormFields,
    isRolePagePermissionPage,
    roleFieldName,
    roleOptionsByEnterprize,
    selectedEnterprizeKey,
  ]);
  const orderedFields = orderFormFields(formFields);
  const isProductManagement = pageCode === "product_management";
  const isPartManagement = pageCode === "part_management";
  const isQuotation = pageCode === "quotation";
  const isPurchaseOrder = pageCode === "purchase_order";
  const isImageManagement =
    pageCode === "image_management" || customFormToken === "imagemanagement";
  const partCatalog = config?.meta?.part_catalog ?? EMPTY_ARRAY;
  const productOptions = (config?.meta?.product_options || []).map((option) => ({
    value: option.value,
    label: option.label,
    default_volume: option.default_volume,
    default_rate: option.default_rate,
  }));
  const quotationCatalog = config?.meta?.quotation_catalog ?? EMPTY_ARRAY;
  const fallbackSectionTitle =
    pageCode === "enterprize" ? "Enterprize Details" : "";
  const formSections = groupFieldsBySection(orderedFields, fallbackSectionTitle);
  const readOnly = mode === "View";
  const isEditMode = mode === "Edit";
  const isPoStepOne = isPurchaseOrder && poStep === 1;
  const isPoStepTwo = isPurchaseOrder && poStep === 2;
  const isPoStepThree = isPurchaseOrder && poStep === 3;
  const quotationCostFields = [
    { name: "manufacturing_cost", label: "Manufacturing Cost" },
    { name: "packaging_cost", label: "Packaging Cost" },
    { name: "fob_cost", label: "FOB Cost" },
    { name: "finishing_cost", label: "Finishing Cost" },
    { name: "tax", label: "Tax" },
    { name: "total_amount", label: "Total", disabled: true },
  ];

  const normalizeSelectValue = (field, rawValue) => {
    if (!field?.options?.length) return rawValue;
    const selectedOption = field.options.find(
      (option) => String(option.value) === String(rawValue),
    );
    return selectedOption ? selectedOption.value : rawValue;
  };

  const getCatalogEntry = (partManagementFid) =>
    partCatalog.find((item) => String(item.value) === String(partManagementFid));

  const applySpecToProductRow = (row, spec = {}) => ({
    ...row,
    part_type: spec.part_type || row.part_type || "",
    radius: spec.radius ?? row.radius ?? "",
    length: spec.length ?? row.length ?? "",
    width: spec.width ?? row.width ?? "",
    height: spec.height ?? row.height ?? "",
    unit: spec.unit ?? row.unit ?? "",
    volume: spec.volume ?? row.volume ?? "",
    rate: spec.rate ?? row.rate ?? "",
  });

  const normalizeComputedRow = (row, previousUnit = "") => {
    const currentUnit = normalizeUnit(row.unit);
    const priorUnit = normalizeUnit(previousUnit);

    let nextRow = { ...row };
    if (priorUnit && currentUnit && priorUnit !== currentUnit) {
      nextRow = {
        ...nextRow,
        radius: convertDimensionValue(nextRow.radius, priorUnit, currentUnit),
        length: convertDimensionValue(nextRow.length, priorUnit, currentUnit),
        width: convertDimensionValue(nextRow.width, priorUnit, currentUnit),
        height: convertDimensionValue(nextRow.height, priorUnit, currentUnit),
      };
    }

    const rowWithConsistentDimensions = clearInactiveDimensions(nextRow, nextRow.part_type);
    return {
      ...rowWithConsistentDimensions,
      volume: calculateVolumeFromRow(rowWithConsistentDimensions),
    };
  };

  const handleChange = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (isRolePagePermissionPage && name === "enterprize_fid") {
        next[roleFieldName] = "";
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      if (isRolePagePermissionPage && name === "enterprize_fid" && next[roleFieldName]) {
        delete next[roleFieldName];
      }
      return next;
    });
  };

  const handleProductPartChange = (rowIndex, fieldName, rawValue) => {
    setProductPartRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;
        const nextRow = { ...row, [fieldName]: rawValue };

        if (fieldName === "part_management_fid") {
          const catalogEntry = getCatalogEntry(rawValue);
          const firstSpec = (catalogEntry?.specs || [])[0] || {};
          const rowWithSpec = applySpecToProductRow(
            {
              ...nextRow,
              part_name: catalogEntry?.part_name || "",
            },
            firstSpec,
          );
          return normalizeComputedRow(rowWithSpec);
        }

        if (fieldName === "part_type" && nextRow.part_management_fid) {
          const catalogEntry = getCatalogEntry(nextRow.part_management_fid);
          const matchedSpec = (catalogEntry?.specs || []).find(
            (spec) =>
              String(spec.part_type || "").toLowerCase() ===
              String(rawValue || "").toLowerCase(),
          );
          if (matchedSpec) return normalizeComputedRow(applySpecToProductRow(nextRow, matchedSpec));
        }

        return normalizeComputedRow(nextRow, fieldName === "unit" ? row.unit : "");
      }),
    );
  };

  const addProductPartRow = () => {
    setProductPartRows((prev) => [...prev, { ...PRODUCT_PART_DEFAULT }]);
  };

  const removeProductPartRow = (rowIndex) => {
    setProductPartRows((prev) => {
      const next = prev.filter((_, index) => index !== rowIndex);
      return next.length ? next : [{ ...PRODUCT_PART_DEFAULT }];
    });
  };

  const handlePartSpecificationChange = (rowIndex, fieldName, value) => {
    setPartSpecificationRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;
        if (fieldName === "part_type") {
          return normalizeComputedRow({
            ...PART_SPECIFICATION_DEFAULT,
            ...row,
            part_type: value,
          });
        }
        return normalizeComputedRow(
          { ...row, [fieldName]: value },
          fieldName === "unit" ? row.unit : "",
        );
      }),
    );
  };

  const addPartSpecificationRow = () => {
    setPartSpecificationRows((prev) => [...prev, { ...PART_SPECIFICATION_DEFAULT }]);
  };

  const removePartSpecificationRow = (rowIndex) => {
    setPartSpecificationRows((prev) => {
      const next = prev.filter((_, index) => index !== rowIndex);
      return next.length ? next : [{ ...PART_SPECIFICATION_DEFAULT }];
    });
  };

  const handleQuotationItemChange = (rowIndex, fieldName, rawValue) => {
    setQuotationItems((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;
        const nextRow = { ...row, [fieldName]: rawValue };
        if (fieldName === "product_management_fid") {
          const product = productOptions.find(
            (option) => String(option.value) === String(rawValue),
          );
          nextRow.product_name = product?.label || "";
          nextRow.volume = String(Number(product?.default_volume || 0).toFixed(2));
          nextRow.rate = String(Number(product?.default_rate || 0).toFixed(2));
          nextRow.quantity = nextRow.quantity ? String(nextRow.quantity) : "1";
        }
        const quantity = Number(nextRow.quantity || 0);
        const rate = Number(nextRow.rate || 0);
        nextRow.amount =
          Number.isFinite(quantity) && Number.isFinite(rate) && quantity > 0 && rate > 0
            ? String((quantity * rate).toFixed(2))
            : "";
        return nextRow;
      }),
    );
  };

  const addQuotationItemRow = () => {
    setQuotationItems((prev) => [...prev, { ...QUOTATION_ITEM_DEFAULT }]);
  };

  const removeQuotationItemRow = (rowIndex) => {
    setQuotationItems((prev) => {
      const next = prev.filter((_, index) => index !== rowIndex);
      return next.length ? next : [{ ...QUOTATION_ITEM_DEFAULT }];
    });
  };

  const buildPoRowsFromQuotations = (quotations = []) =>
    (Array.isArray(quotations) ? quotations : []).flatMap((quotation) =>
      (quotation.items || [])
        .map((item) => {
          const quotationQty = Number(item.quantity || 0);
          const poQty = Number(item.consumed_quantity || item.po_quantity || 0);
          const remainingQty = Math.max(quotationQty - poQty, 0);
          const rate = Number(item.rate || 0);
          return {
            ...PO_ITEM_DEFAULT,
            quotation_fid: quotation.quotation_id,
            quotation_code: quotation.quotation_code,
            quotation_item_fid: item.quotation_item_id,
            product_management_fid: item.product_management_fid,
            product_name: item.product_name,
            volume: item.volume ?? "",
            quoted_quantity: String(quotationQty),
            consumed_quantity: String(poQty),
            remaining_quantity: String(remainingQty),
            quantity: remainingQty > 0 ? String(remainingQty) : "0",
            rate: String(rate),
            amount: String(remainingQty > 0 ? remainingQty * rate : 0),
          };
        }),
    );

  const syncPoSelectionState = (nextSelections) => {
    setSelectedPoQuotations(nextSelections);
    setSelectedQuotation(nextSelections[nextSelections.length - 1] || null);
    setPoItems(buildPoRowsFromQuotations(nextSelections));
    const primaryQuotationId = nextSelections.length ? nextSelections[0].quotation_id : "";
    setFormData((prev) => ({
      ...prev,
      quotation_fid: primaryQuotationId,
      purchase_order_progress_status: "incomplete",
    }));
  };

  const handleToggleQuotationSelectionForPo = (quotation) => {
    const exists = selectedPoQuotations.some(
      (row) => String(row.quotation_id) === String(quotation.quotation_id),
    );
    const nextSelections = exists
      ? selectedPoQuotations.filter(
          (row) => String(row.quotation_id) !== String(quotation.quotation_id),
        )
      : [...selectedPoQuotations, quotation];
    syncPoSelectionState(nextSelections);
  };

  const handlePoItemChange = (rowIndex, fieldName, rawValue) => {
    setPoItems((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;
        const nextRow = { ...row, [fieldName]: rawValue };
        const remainingQty = Number(nextRow.remaining_quantity || 0);
        let quantity = Number(nextRow.quantity || 0);
        const rate = Number(nextRow.rate || 0);
        if (!Number.isFinite(quantity) || quantity < 0) quantity = 0;
        if (quantity > remainingQty) quantity = remainingQty;
        nextRow.quantity = String(quantity);
        nextRow.amount =
          Number.isFinite(quantity) && Number.isFinite(rate) && quantity >= 0 && rate > 0
            ? String(quantity * rate)
            : "";
        return nextRow;
      }),
    );
  };

  const validateForm = () => {
    const nextErrors = {};
    const isInvalidRate = (value) => {
      const numericRate = Number(value);
      return !Number.isFinite(numericRate) || numericRate <= 0;
    };

    formFields.forEach((field) => {
      const value = formData[field.name];
      const isPasswordField = field.type === "password";
      const isEditMode = mode === "Edit";

      if (field.required) {
        const hasFileValue =
          field.type === "file" &&
          value &&
          (typeof value === "string" ? value.trim() !== "" : Boolean(value?.name));
        const missingValue =
          value === undefined ||
          value === null ||
          value === "" ||
          (field.type === "file" && !hasFileValue);
        if (missingValue && !(isEditMode && isPasswordField)) {
          nextErrors[field.name] = `${field.label} is required`;
        }
      }

      if (field.type === "email" && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          nextErrors[field.name] = "Invalid email address";
        }
      }

      if (isPasswordField && value && value.length < 6) {
        nextErrors[field.name] = "Password must be at least 6 characters";
      }
    });

    if (isProductManagement) {
      const validRows = productPartRows.filter(
        (row) =>
          row.part_management_fid ||
          row.part_type ||
          row.radius ||
          row.length ||
          row.width ||
          row.height ||
          row.volume ||
          row.rate,
      );

      if (validRows.length === 0) {
        nextErrors.part_details = "At least one part row is required";
      } else {
        validRows.forEach((row, rowIndex) => {
          if (!row.part_management_fid) {
            nextErrors[`part_details_${rowIndex}_part_management_fid`] =
              "Part Name is required";
          }
          if (isInvalidRate(row.rate)) {
            nextErrors[`part_details_${rowIndex}_rate`] =
              "Rate is required and must be greater than 0";
          }
        });
      }
    }

    if (isPartManagement) {
      const validRows = partSpecificationRows.filter(
        (row) =>
          row.part_type ||
          row.radius ||
          row.length ||
          row.width ||
          row.height ||
          row.unit ||
          row.volume ||
          row.rate,
      );

      if (validRows.length === 0) {
        nextErrors.part_specifications =
          "At least one part specification row is required";
      } else {
        validRows.forEach((row, rowIndex) => {
          if (!row.part_type) {
            nextErrors[`part_specifications_${rowIndex}_part_type`] =
              "Part Type is required";
          }
          if (isInvalidRate(row.rate)) {
            nextErrors[`part_specifications_${rowIndex}_rate`] =
              "Rate is required and must be greater than 0";
          }
        });
      }
    }

    if (isQuotation) {
      const validRows = quotationItems.filter(
        (row) => row.product_management_fid || row.quantity || row.rate,
      );
      if (validRows.length === 0) {
        nextErrors.quotation_items = "At least one quotation item is required";
      } else {
        validRows.forEach((row, rowIndex) => {
          if (!row.product_management_fid) {
            nextErrors[`quotation_items_${rowIndex}_product_management_fid`] =
              "Product Name is required";
          }
          if (isInvalidRate(row.quantity)) {
            nextErrors[`quotation_items_${rowIndex}_quantity`] =
              "Quantity is required and must be greater than 0";
          }
          if (isInvalidRate(row.rate)) {
            nextErrors[`quotation_items_${rowIndex}_rate`] =
              "Rate is required and must be greater than 0";
          }
        });
      }

      ["manufacturing_cost", "packaging_cost", "fob_cost", "finishing_cost", "tax"].forEach(
        (fieldName) => {
          const value = formData[fieldName];
          if (value === undefined || value === null || value === "") return;
          if (Number(value) < 0 || Number.isNaN(Number(value))) {
            nextErrors[fieldName] = "Value must be a valid non-negative number";
          }
        },
      );
    }

    if (isPurchaseOrder) {
      const hasSelectedQuotation =
        selectedPoQuotations.length ||
        poItems.some((row) => row.quotation_fid || row.quotation_item_fid);
      if (!hasSelectedQuotation) {
        nextErrors.quotation_fid = "Please select at least one quotation";
      }
      const eligibleRows = poItems.filter((row) => Number(row.remaining_quantity || 0) > 0);
      if (!poItems.length || !eligibleRows.length) {
        nextErrors.po_items = "At least one selected product is required";
      } else {
        poItems.forEach((row, rowIndex) => {
          const remainingQty = Number(row.remaining_quantity || 0);
          const orderNowQty = Number(row.quantity || 0);
          if (remainingQty <= 0) return;
          if (isInvalidRate(orderNowQty)) {
            nextErrors[`po_items_${rowIndex}_quantity`] =
              "Quantity is required and must be greater than 0";
          } else if (orderNowQty > remainingQty) {
            nextErrors[`po_items_${rowIndex}_quantity`] =
              "Order now quantity cannot exceed remaining quantity";
          }
        });
      }
    }

    return nextErrors;
  };

  useEffect(() => {
    if (!show || !config) return;

    if (data && (mode === "Edit" || mode === "View")) {
      setFormData(data);

      if (isProductManagement) {
        const rows = Array.isArray(data.part_details) && data.part_details.length
          ? data.part_details.map((row) => ({
              ...PRODUCT_PART_DEFAULT,
              ...row,
            }))
          : [PRODUCT_PART_DEFAULT];
        setProductPartRows(rows.map((row) => normalizeComputedRow(row)));
      }

      if (isPartManagement) {
        const rows =
          Array.isArray(data.part_specifications) && data.part_specifications.length
            ? data.part_specifications.map((row) => ({
                ...PART_SPECIFICATION_DEFAULT,
                ...row,
              }))
            : [PART_SPECIFICATION_DEFAULT];
        setPartSpecificationRows(rows.map((row) => normalizeComputedRow(row)));
      }
      if (isQuotation) {
        const rows =
          Array.isArray(data.quotation_items) && data.quotation_items.length
            ? data.quotation_items.map((row) => ({
                ...QUOTATION_ITEM_DEFAULT,
                ...row,
              }))
            : [QUOTATION_ITEM_DEFAULT];
        setQuotationItems(rows);
        setFormData((prev) => ({
          ...prev,
          ...getQuotationCostValues(data, rows),
        }));
      }
      if (isPurchaseOrder) {
        const rows =
          Array.isArray(data.po_items) && data.po_items.length
            ? data.po_items.map((row) => ({
                ...PO_ITEM_DEFAULT,
                ...row,
              }))
            : [];
        setPoItems(rows);
        const selectedQuotationIds = [
          data.quotation_fid,
          ...rows.map((row) => row.quotation_fid),
        ]
          .filter((value) => value !== null && value !== undefined && value !== "")
          .map((value) => String(value));
        const uniqueIds = [...new Set(selectedQuotationIds)];
        const selectedRows = uniqueIds.map((quotationId) => {
          return (
            quotationCatalog.find((q) => String(q.quotation_id) === String(quotationId)) || {
              quotation_id: quotationId,
              quotation_code:
                uniqueIds.length === 1 ? data.quotation_code : data.quotation_codes || data.quotation_code,
              customer_name:
                uniqueIds.length === 1 ? data.customer_name : data.customer_names || data.customer_name,
              items: [],
            }
          );
        });
        setSelectedQuotation(selectedRows[selectedRows.length - 1] || null);
        setSelectedPoQuotations(selectedRows);
        setPoStep(readOnly ? 3 : 2);
      }
    } else {
      const initial = {};
      baseFormFields.forEach((field) => {
        initial[field.name] = field.defaultValue || "";
      });
      if (isQuotation && !initial.quotation_date) {
        initial.quotation_date = getTodayDateForInput();
      }
      setFormData(initial);

      if (isProductManagement) setProductPartRows([normalizeComputedRow(PRODUCT_PART_DEFAULT)]);
      if (isPartManagement) {
        setPartSpecificationRows([normalizeComputedRow(PART_SPECIFICATION_DEFAULT)]);
      }
      if (isQuotation) {
        const defaultRows = [{ ...QUOTATION_ITEM_DEFAULT }];
        setQuotationItems(defaultRows);
        setFormData((prev) => ({
          ...prev,
          ...QUOTATION_COST_DEFAULTS,
          ...getQuotationCostValues(
            {
              ...QUOTATION_COST_DEFAULTS,
              ...prev,
            },
            defaultRows,
          ),
        }));
      }
      if (isPurchaseOrder) {
        setPoItems([]);
        setSelectedQuotation(null);
        setSelectedPoQuotations([]);
        setPoStep(1);
        setFormData((prev) => ({
          ...prev,
          purchase_order_progress_status:
            prev.purchase_order_progress_status || "incomplete",
        }));
      }
    }

    setErrors({});
  }, [
    show,
    data,
    config,
    mode,
    baseFormFields,
    isProductManagement,
    isPartManagement,
    isQuotation,
    isPurchaseOrder,
    quotationCatalog,
    readOnly,
  ]);

  useEffect(() => {
    if (!show || !isQuotation) return;

    const nextCostValues = getQuotationCostValues(formData, quotationItems);
    setFormData((prev) => {
      const isUnchanged =
        prev.manufacturing_cost === nextCostValues.manufacturing_cost &&
        prev.packaging_cost === nextCostValues.packaging_cost &&
        prev.fob_cost === nextCostValues.fob_cost &&
        prev.finishing_cost === nextCostValues.finishing_cost &&
        prev.tax === nextCostValues.tax &&
        prev.total_amount === nextCostValues.total_amount;
      if (isUnchanged) return prev;
      return {
        ...prev,
        ...nextCostValues,
      };
    });
  }, [
    show,
    isQuotation,
    quotationItems,
    formData.manufacturing_cost,
    formData.packaging_cost,
    formData.fob_cost,
    formData.finishing_cost,
    formData.tax,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.values(validationErrors)[0];
      showAlert("error", firstError);
      return;
    }

    if (isProductManagement) {
      const part_details = productPartRows
        .filter(
          (row) =>
            row.part_management_fid ||
            row.part_type ||
            row.radius ||
            row.length ||
            row.width ||
            row.height ||
            row.volume ||
            row.rate,
        )
        .map((row) => ({
          ...row,
          part_management_fid: row.part_management_fid || null,
          part_name: row.part_name || null,
        }));
      onSave({ ...formData, part_details });
      return;
    }

    if (isPartManagement) {
      const part_specifications = partSpecificationRows
        .filter(
          (row) =>
            row.part_type ||
            row.radius ||
            row.length ||
            row.width ||
            row.height ||
            row.unit ||
            row.volume ||
            row.rate,
        )
        .map((row) => ({
          ...row,
          part_type: row.part_type || null,
        }));
      onSave({ ...formData, part_specifications });
      return;
    }

    if (isQuotation) {
      const payloadRows = quotationItems
        .filter((row) => row.product_management_fid || row.quantity || row.rate)
        .map((row) => ({
          product_management_fid: row.product_management_fid,
          volume: row.volume || null,
          quantity: row.quantity,
          rate: row.rate,
          amount: row.amount,
        }));
      const costs = getQuotationCostValues(formData, payloadRows);
      onSave({
        ...formData,
        ...costs,
        quotation_items: payloadRows,
      });
      return;
    }

    if (isPurchaseOrder) {
      const payloadRows = poItems
        .filter((row) => Number(row.quantity || 0) > 0 && Number(row.remaining_quantity || 0) > 0)
        .map((row) => ({
          quotation_item_fid: row.quotation_item_fid,
          product_management_fid: row.product_management_fid,
          volume: row.volume || null,
          quantity: row.quantity,
          rate: row.rate,
          amount: row.amount,
        }));
      const selectedQuotationIds = [
        ...new Set(
          [
            ...selectedPoQuotations.map((row) => row.quotation_id),
            ...poItems.map((row) => row.quotation_fid),
          ]
            .filter((value) => value !== null && value !== undefined && value !== "")
            .map((value) => String(value)),
        ),
      ];
      if (!payloadRows.length) {
        showAlert("error", "Please enter Order Now quantity for at least one product");
        return;
      }
      onSave({
        ...formData,
        quotation_fid: formData.quotation_fid || selectedQuotationIds[0] || "",
        quotation_fids: selectedQuotationIds,
        po_items: payloadRows,
      });
      return;
    }

    onSave(formData);
  };

  const getActionLabel = () => {
    if (isImageManagement && mode === "Add") return "Upload Image";
    if (config?.submitLabel) return config.submitLabel;
    if (config?.submit_label) return config.submit_label;
    if (mode === "Add") return "Submit";
    if (mode === "Edit") return "Update";
    return "Save";
  };

  const getTitle = () => {
    if (!config?.title) return mode;
    return `${mode} ${config.title}`;
  };

  return (
    <Modal isOpen={show} onClose={onClose} title={getTitle()} size="large">
      <Form onSubmit={handleSubmit}>
        {isProductManagement ? (
          <ProductManagementModalForm
            formFields={formFields}
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            partCatalog={partCatalog}
            productPartRows={productPartRows}
            getCatalogEntry={getCatalogEntry}
            handleProductPartChange={handleProductPartChange}
            addProductPartRow={addProductPartRow}
            removeProductPartRow={removeProductPartRow}
            getFieldLabel={getFieldLabel}
            normalizeSelectValue={normalizeSelectValue}
            handleChange={handleChange}
            isEditMode={isEditMode}
          />
        ) : isPartManagement ? (
          <PartManagementModalForm
            formFields={formFields}
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            partSpecificationRows={partSpecificationRows}
            getFieldLabel={getFieldLabel}
            normalizeSelectValue={normalizeSelectValue}
            handleChange={handleChange}
            handlePartSpecificationChange={handlePartSpecificationChange}
            addPartSpecificationRow={addPartSpecificationRow}
            removePartSpecificationRow={removePartSpecificationRow}
            />
        ) : isQuotation ? (
          <QuotationModalForm
            formFields={formFields}
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            productOptions={productOptions}
            quotationItems={quotationItems}
            handleQuotationItemChange={handleQuotationItemChange}
            addQuotationItemRow={addQuotationItemRow}
            removeQuotationItemRow={removeQuotationItemRow}
            getFieldLabel={getFieldLabel}
            normalizeSelectValue={normalizeSelectValue}
            handleChange={handleChange}
            costFields={quotationCostFields}
          />
        ) : isPurchaseOrder ? (
          <PurchaseOrderModalForm
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            quotationCatalog={quotationCatalog}
            selectedQuotation={selectedQuotation}
            selectedQuotationIds={selectedPoQuotations.map((row) =>
              String(row.quotation_id),
            )}
            onToggleQuotationSelection={handleToggleQuotationSelectionForPo}
            poItems={poItems}
            handlePoItemChange={handlePoItemChange}
            step={poStep}
            setStep={setPoStep}
            handleChange={handleChange}
          />
        ) : isImageManagement ? (
          <ImageManagementUploadForm
            formFields={formFields}
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            getFieldLabel={getFieldLabel}
            handleChange={handleChange}
          />
        ) : (
          <GenericPageModalForm
            formSections={formSections}
            formData={formData}
            errors={errors}
            readOnly={readOnly}
            getFieldLabel={getFieldLabel}
            normalizeSelectValue={normalizeSelectValue}
            handleChange={handleChange}
          />
        )}

        <div className={styles.buttonGroup}>
          {isPoStepOne && !readOnly ? (
            <Button
              type="button"
              variant="primary"
              className="w-25"
              onClick={() => setPoStep(2)}
              disabled={!selectedPoQuotations.length}
            >
              Next
            </Button>
          ) : isPoStepTwo && !readOnly ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPoStep(1)}
                className="w-25"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="primary"
                className="w-25"
                onClick={() => setPoStep(3)}
                disabled={!poItems.some((row) => Number(row.quantity || 0) > 0)}
              >
                Next
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={isPoStepThree && !readOnly ? () => setPoStep(2) : onClose}
              className="w-25"
            >
              {isPoStepThree && !readOnly ? "Previous" : "Cancel"}
            </Button>
          )}

          {!readOnly && (
            (!isPurchaseOrder || poStep === 3) && (
              <Button type="submit" variant="primary" className="w-25">
                {getActionLabel()}
              </Button>
            )
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default PageModal;
